import { useEffect, useState } from 'react';
import { View, StyleSheet, Text, ScrollView, ActivityIndicator, Image, TouchableOpacity, Share, Alert, Platform } from 'react-native';
import * as FileSystem from 'expo-file-system';
import * as Sharing from 'expo-sharing';
import { useRoute, RouteProp, useNavigation } from '@react-navigation/native';
import { RootStackParamList } from '../types/navigation';
import { VideoPost } from '../types/video';
import VideoPlayer from '../components/video/VideoPlayer';
import VideoCard from '../components/video/VideoCard';
import CommentsModal from '../components/video/CommentsModal';
import { searchVideoPosts, getAuthorVideoPosts, getProfile, followUser, unfollowUser, getPostThread, likePost, unlikePost, repost, unrepost } from '../services/bluesky';
import { convertPostToVideoPost, formatViews, formatRelativeTime } from '../utils/video';
import { getCache, setCache, getCacheKey, CACHE_DURATION } from '../utils/cache';
import { Ionicons } from '@expo/vector-icons';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useSavedVideosStore } from '../store/savedVideosStore';
import { useHistoryStore } from '../store/historyStore';
import { ActionSheet } from '../components/common/ActionSheet';

type VideoPlaybackRouteProp = RouteProp<RootStackParamList, 'VideoPlayback'>;
type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

interface Comment {
  id: string;
  authorName: string;
  authorHandle: string;
  authorAvatar?: string;
  authorDid: string;
  text: string;
  createdAt: string;
  likeCount: number;
  uri: string;
  cid: string;
  likeUri?: string;
  facets?: any[];
  embed?: any;
}

const VideoPlaybackScreen = () => {
  const route = useRoute<VideoPlaybackRouteProp>();
  const navigation = useNavigation<NavigationProp>();
  const { videoId, video: initialVideo } = route.params;
  const [video, setVideo] = useState<VideoPost | null>(initialVideo || null);
  const [relatedVideos, setRelatedVideos] = useState<VideoPost[]>([]);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(!initialVideo);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followUri, setFollowUri] = useState<string | undefined>(undefined);
  const [followLoading, setFollowLoading] = useState(false);
  const [likeLoading, setLikeLoading] = useState(false);
  const [repostLoading, setRepostLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [commentsModalVisible, setCommentsModalVisible] = useState(false);
  const [repostSheetVisible, setRepostSheetVisible] = useState(false);
  
  const { addToHistory } = useHistoryStore();
  
  const { saveVideo, unsaveVideo, isVideoSaved, loadSavedVideos } = useSavedVideosStore();
  const isSaved = video ? isVideoSaved(video.id) : false;

  useEffect(() => {
    fetchVideoData();
    if (video) {
      fetchComments(video.originalUri || video.uri);
      addToHistory(video);
    }
  }, [videoId, video?.uri, video?.originalUri]);

  useEffect(() => {
    if (video) {
      checkFollowStatus();
    }
  }, [video]);

  useEffect(() => {
    loadSavedVideos();
  }, []);

  const checkFollowStatus = async () => {
    if (!video) return;
    
    try {
      // キャッシュを確認
      const cacheKey = getCacheKey.profile(video.authorDid);
      const cachedProfile = await getCache<any>(cacheKey, CACHE_DURATION.PROFILE);
      
      if (cachedProfile) {
        setIsFollowing(!!cachedProfile.viewer?.following);
        setFollowUri(cachedProfile.viewer?.following);
        return;
      }
      
      const profile = await getProfile(video.authorDid);
      setIsFollowing(!!profile.viewer?.following);
      setFollowUri(profile.viewer?.following);
      
      // キャッシュに保存
      await setCache(cacheKey, profile);
    } catch (error) {
      console.error('Failed to check follow status:', error);
    }
  };

  const handleFollowToggle = async () => {
    if (!video || followLoading) return;

    try {
      setFollowLoading(true);
      
      if (isFollowing && followUri) {
        await unfollowUser(followUri);
        setIsFollowing(false);
        setFollowUri(undefined);
      } else {
        const uri = await followUser(video.authorDid);
        setIsFollowing(true);
        setFollowUri(uri);
      }
    } catch (error) {
      console.error('Failed to toggle follow:', error);
    } finally {
      setFollowLoading(false);
    }
  };

  const handleShare = async () => {
    if (!video) return;
    try {
      const url = `https://bsky.app/profile/${video.channelHandle}/post/${video.uri.split('/').pop()}`;
      await Share.share({
        message: `${video.title}\n${url}`,
        url: url,
        title: video.title,
      });
    } catch (error) {
      console.error('Share error:', error);
    }
  };

  const handleDownload = async () => {
    if (!video || downloading) return;
    
    // YouTube動画はダウンロードできない
    if (video.type === 'youtube') {
      Alert.alert('ダウンロード不可', 'YouTube動画は直接ダウンロードできません。YouTubeアプリをご利用ください。');
      return;
    }

    try {
      setDownloading(true);
      
      // Web環境の場合
      if (Platform.OS === 'web') {
        // ブラウザのダウンロード機能を使用
        const link = document.createElement('a');
        link.href = video.videoUrl;
        link.download = `${video.title.replace(/[^a-z0-9]/gi, '_')}.mp4`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        Alert.alert('ダウンロード開始', 'ブラウザのダウンロードが開始されました。');
      } else {
        // モバイルの場合
        const fileUri = FileSystem.documentDirectory + `${video.id}.mp4`;
        const downloadResumable = FileSystem.createDownloadResumable(
          video.videoUrl,
          fileUri
        );
        
        const result = await downloadResumable.downloadAsync();
        if (result) {
          // ダウンロード完了後、共有ダイアログを表示
          if (await Sharing.isAvailableAsync()) {
            await Sharing.shareAsync(result.uri);
          } else {
            Alert.alert('ダウンロード完了', `動画を保存しました: ${result.uri}`);
          }
        }
      }
    } catch (error) {
      console.error('Download error:', error);
      Alert.alert('ダウンロードエラー', '動画のダウンロードに失敗しました。');
    } finally {
      setDownloading(false);
    }
  };

  const handleLikeToggle = async () => {
    if (!video || likeLoading) return;

    try {
      setLikeLoading(true);
      const isLiked = !!video.likeUri;
      
      if (isLiked && video.likeUri) {
        await unlikePost(video.likeUri);
        setVideo({
          ...video,
          likeUri: undefined,
          likeCount: Math.max(0, video.likeCount - 1),
        });
      } else {
        const uri = await likePost(video.originalUri || video.uri, video.originalCid || video.cid);
        setVideo({
          ...video,
          likeUri: uri,
          likeCount: video.likeCount + 1,
        });
      }
    } catch (error) {
      console.error('Failed to toggle like:', error);
      Alert.alert('エラー', '高評価の切り替えに失敗しました。');
    } finally {
      setLikeLoading(false);
    }
  };

  const handleRepostToggle = async () => {
    if (!video || repostLoading) return;

    try {
      setRepostLoading(true);
      const isReposted = !!video.repostUri;
      
      if (isReposted && video.repostUri) {
        await unrepost(video.repostUri);
        setVideo({
          ...video,
          repostUri: undefined,
          repostCount: Math.max(0, video.repostCount - 1),
        });
        Alert.alert('リポスト解除', 'リポストを解除しました。');
      } else {
        const uri = await repost(video.originalUri || video.uri, video.originalCid || video.cid);
        setVideo({
          ...video,
          repostUri: uri,
          repostCount: video.repostCount + 1,
        });
        Alert.alert('リポスト完了', 'リポストしました。');
      }
    } catch (error) {
      console.error('Failed to toggle repost:', error);
      Alert.alert('エラー', 'リポストの切り替えに失敗しました。');
    } finally {
      setRepostLoading(false);
    }
  };

  const handleQuotePost = () => {
    if (!video) return;
    // 引用投稿画面に遷移
    navigation.navigate('Compose', {
      quoteTo: {
        uri: video.originalUri || video.uri,
        cid: video.originalCid || video.cid,
        author: {
          did: video.authorDid,
          handle: video.channelHandle,
          displayName: video.channelName,
          avatar: video.channelAvatar,
        },
        text: video.description || video.title,
      },
    });
  };

  const handleRepostPress = () => {
    setRepostSheetVisible(true);
  };

  const handleCommentLikeToggle = async (comment: Comment) => {
    try {
      if (comment.likeUri) {
        await unlikePost(comment.likeUri);
        setComments(prev => prev.map(c => 
          c.id === comment.id 
            ? { ...c, likeUri: undefined, likeCount: Math.max(0, c.likeCount - 1) } 
            : c
        ));
      } else {
        const uri = await likePost(comment.uri, comment.cid);
        setComments(prev => prev.map(c => 
          c.id === comment.id 
            ? { ...c, likeUri: uri, likeCount: c.likeCount + 1 } 
            : c
        ));
      }
    } catch (error) {
      console.error('Failed to toggle comment like:', error);
    }
  };

  const handleCommentReply = (comment: Comment) => {
    // コメントへの返信画面に遷移
    setCommentsModalVisible(false);
    navigation.navigate('Compose', {
      replyTo: {
        uri: comment.uri,
        cid: comment.cid,
        rootUri: video?.originalUri || video?.uri,
        rootCid: video?.originalCid || video?.cid,
        authorHandle: comment.authorHandle,
      },
    });
  };

  const handleSaveToggle = async () => {
    if (!video) return;
    try {
      if (isSaved) {
        await unsaveVideo(video.id);
        Alert.alert('保存解除', 'プレイリストから削除しました。');
      } else {
        await saveVideo(video);
        Alert.alert('保存完了', 'プレイリストに追加しました。\nマイページから確認できます。');
      }
    } catch (error) {
      console.error('Save toggle error:', error);
      Alert.alert('エラー', '保存の切り替えに失敗しました。');
    }
  };

  const fetchComments = async (targetUri?: string) => {
    const uri = targetUri || video?.originalUri || video?.uri;
    if (!uri) return;
    
    try {
      // まずキャッシュを確認
      const cacheKey = getCacheKey.postThread(uri);
      const cachedComments = await getCache<Comment[]>(cacheKey, CACHE_DURATION.POST_THREAD);
      
      if (cachedComments) {
        setComments(cachedComments);
        return;
      }
      
      const thread = await getPostThread(uri);
      if (thread && thread.replies) {
        const mappedComments = thread.replies
          .filter((reply: any) => reply.post) // 削除済みの投稿などを除外
          .map((reply: any) => ({
            id: reply.post.cid,
            authorName: reply.post.author.displayName || reply.post.author.handle,
            authorHandle: reply.post.author.handle,
            authorAvatar: reply.post.author.avatar,
            authorDid: reply.post.author.did,
            text: reply.post.record.text,
            createdAt: formatRelativeTime(reply.post.record.createdAt),
            likeCount: reply.post.likeCount || 0,
            uri: reply.post.uri,
            cid: reply.post.cid,
            likeUri: reply.post.viewer?.like,
            facets: reply.post.record.facets,
            embed: reply.post.embed,
          }));
        setComments(mappedComments);
        // キャッシュに保存
        await setCache(cacheKey, mappedComments);
      }
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    }
  };

  const fetchVideoData = async () => {
    try {
      if (!video) {
        setLoading(true);
      }
      
      let videoData: VideoPost[] = [];
      
      // 1. まず現在の投稿主の他の動画を取得（YouTubeの「このチャンネルの他の動画」のように）
      if (video) {
        try {
          const authorResult = await getAuthorVideoPosts(video.channelHandle);
          videoData = authorResult.feed
            .map(convertPostToVideoPost)
            .filter((v): v is VideoPost => v !== null && v.id !== videoId);
        } catch (e) {
          console.warn('Failed to fetch author videos, falling back to search', e);
        }
      }

      // 2. 投稿主の動画が少ない場合は検索結果（グローバル）で補完
      if (videoData.length < 5) {
        const result = await searchVideoPosts('');
        const searchData = result.feed
          .map(convertPostToVideoPost)
          .filter((v): v is VideoPost => v !== null && v.id !== videoId);
        
        // 重複を避けて結合
        const existingIds = new Set(videoData.map(v => v.id));
        const filteredSearchData = searchData.filter(v => !existingIds.has(v.id));
        videoData = [...videoData, ...filteredSearchData];
      }

      // 初期データがない場合（IDのみで遷移した場合）、取得したデータから現在のを設定
      if (!video) {
        let currentVideo: VideoPost | null = videoData.find(v => v.id === videoId) || null;
        
        // 見つからない場合は、CIDで直接検索を試みる
        if (!currentVideo) {
          try {
            const searchResult = await searchVideoPosts(videoId);
            currentVideo = searchResult.feed
              .map(convertPostToVideoPost)
              .find((v): v is VideoPost => v !== null && v.id === videoId) || null;
          } catch (e) {
            console.error('Failed to search video by CID:', e);
          }
        }
        
        if (currentVideo) {
          setVideo(currentVideo);
          // videoステートが更新されると、useEffectが走り他のデータ取得も行われます
        }
      }

      // 関連動画を設定（最大15件）
      setRelatedVideos(videoData.slice(0, 15));
    } catch (error) {
      console.error('Failed to fetch video data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#fff" />
      </View>
    );
  }

  if (!video) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>動画が見つかりませんでした</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} stickyHeaderIndices={[]}>
      <View style={styles.playerWrapper}>
        <VideoPlayer 
          videoUrl={video.videoUrl} 
          thumbnailUrl={video.thumbnailUrl} 
          type={video.type}
        />
      </View>
      
      <View style={styles.contentContainer}>
        {/* タイトルとメタ情報 */}
        <Text style={styles.title}>{video.title}</Text>
        <Text style={styles.metaText}>
          {formatViews(video.likeCount)} いいね · {video.createdAt}
        </Text>

        {/* チャンネル情報 */}
        <View style={styles.channelRow}>
          <TouchableOpacity 
            style={styles.channelInfo}
            onPress={() => navigation.navigate('ChannelDetail', { 
              did: video.authorDid, 
              handle: video.channelHandle 
            })}
          >
            {video.channelAvatar ? (
              <Image source={{ uri: video.channelAvatar }} style={styles.avatar} />
            ) : (
              <View style={[styles.avatar, { backgroundColor: '#333' }]} />
            )}
            <View style={styles.channelTextContainer}>
              <Text style={styles.channelName} numberOfLines={1}>
                {video.channelName}
              </Text>
              <Text style={styles.subscribersText}>@{video.channelHandle}</Text>
            </View>
          </TouchableOpacity>
          <TouchableOpacity 
            style={[styles.subscribeButton, isFollowing && styles.subscribeButtonActive]}
            onPress={handleFollowToggle}
            disabled={followLoading}
          >
            <Text style={styles.subscribeButtonText}>
              {followLoading ? '...' : isFollowing ? 'フォロー中' : 'フォロー'}
            </Text>
          </TouchableOpacity>
        </View>

        {/* アクションボタン */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.actionsRow}>
          <TouchableOpacity 
            style={styles.actionButton}
            onPress={handleLikeToggle}
            disabled={likeLoading}
          >
            <Ionicons 
              name={video.likeUri ? "thumbs-up" : "thumbs-up-outline"} 
              size={20} 
              color={video.likeUri ? "#FF0000" : "#fff"} 
            />
            <Text style={[styles.actionButtonText, video.likeUri ? { color: '#FF0000' } : null]}>
              {formatViews(video.likeCount)}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={styles.actionButton}
            onPress={handleRepostPress}
            disabled={repostLoading}
          >
            <Ionicons 
              name={video.repostUri ? "repeat" : "repeat-outline"} 
              size={20} 
              color={video.repostUri ? "#00BA7C" : "#fff"} 
            />
            <Text style={[styles.actionButtonText, video.repostUri ? { color: '#00BA7C' } : null]}>
              {video.repostCount > 0 ? formatViews(video.repostCount) : 'リポスト'}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.actionButton} onPress={handleShare}>
            <Ionicons name="share-social-outline" size={20} color="#fff" />
            <Text style={styles.actionButtonText}>共有</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={styles.actionButton} 
            onPress={handleDownload}
            disabled={downloading}
          >
            <Ionicons name="download-outline" size={20} color="#fff" />
            <Text style={styles.actionButtonText}>
              {downloading ? 'DL中...' : 'オフライン'}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={styles.actionButton} 
            onPress={handleSaveToggle}
          >
            <Ionicons 
              name={isSaved ? "checkmark-circle" : "add-circle-outline"} 
              size={20} 
              color={isSaved ? "#3ea6ff" : "#fff"} 
            />
            <Text style={[styles.actionButtonText, isSaved ? { color: '#3ea6ff' } : null]}>
              {isSaved ? '保存済み' : '保存'}
            </Text>
          </TouchableOpacity>
        </ScrollView>

        {/* コメントセクション */}
        <TouchableOpacity 
          style={styles.commentsSection}
          onPress={() => setCommentsModalVisible(true)}
          activeOpacity={0.7}
        >
          <View style={styles.commentsHeader}>
            <Text style={styles.commentsTitle}>コメント</Text>
            <Text style={styles.commentsCount}>{comments.length}</Text>
          </View>
          
          {comments.length > 0 ? (
            <View style={styles.commentPreview}>
              <View style={styles.commentItem}>
                {comments[0].authorAvatar ? (
                  <Image source={{ uri: comments[0].authorAvatar }} style={styles.commentAvatar} />
                ) : (
                  <View style={[styles.commentAvatar, { backgroundColor: '#333' }]} />
                )}
                <View style={styles.commentTextContainer}>
                  <Text style={styles.commentText} numberOfLines={2}>
                    {comments[0].text}
                  </Text>
                </View>
              </View>
              {comments.length > 1 && (
                <TouchableOpacity 
                  style={styles.viewAllComments}
                  onPress={() => setCommentsModalVisible(true)}
                >
                  <Text style={styles.viewAllText}>すべてのコメントを見る</Text>
                </TouchableOpacity>
              )}
            </View>
          ) : (
            <Text style={styles.noCommentsText}>コメントはありません</Text>
          )}
        </TouchableOpacity>

        {/* 関連動画 */}
        <View style={styles.relatedSection}>
          <Text style={styles.relatedTitle}>関連動画</Text>
          {relatedVideos.map((item) => (
            <VideoCard
              key={item.id}
              thumbnailUrl={item.thumbnailUrl}
              title={item.title}
              channelName={item.channelName}
              channelAvatar={item.channelAvatar}
              likeCount={item.likeCount}
              createdAt={item.createdAt}
              duration={item.duration}
              videoUrl={item.videoUrl}
              videoType={item.type}
              onPress={() => navigation.push('VideoPlayback', { videoId: item.id, video: item })}
            />
          ))}
        </View>
      </View>

      {/* コメントモーダル */}
      <CommentsModal
        visible={commentsModalVisible}
        onClose={() => setCommentsModalVisible(false)}
        comments={comments}
        onLikeComment={handleCommentLikeToggle}
        onReplyComment={handleCommentReply}
      />

      {/* リポストアクションシート */}
      <ActionSheet
        visible={repostSheetVisible}
        onClose={() => setRepostSheetVisible(false)}
        options={[
          {
            label: video?.repostUri ? 'リポストを解除' : 'リポスト',
            icon: 'repeat',
            onPress: handleRepostToggle,
          },
          {
            label: '引用投稿',
            icon: 'chatbox-ellipses-outline',
            onPress: handleQuotePost,
          },
        ]}
      />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000',
  },
  loadingContainer: {
    flex: 1,
    backgroundColor: '#000',
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorContainer: {
    flex: 1,
    backgroundColor: '#000',
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    color: '#fff',
    fontSize: 16,
  },
  playerWrapper: {
    width: '100%',
    zIndex: 10,
  },
  contentContainer: {
    padding: 12,
  },
  title: {
    color: '#fff',
    fontSize: 18,
    fontWeight: 'bold',
    lineHeight: 24,
    marginBottom: 4,
  },
  metaText: {
    color: '#aaa',
    fontSize: 13,
    marginBottom: 16,
  },
  channelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  channelInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    marginRight: 10,
  },
  channelTextContainer: {
    flex: 1,
  },
  channelName: {
    color: '#fff',
    fontSize: 15,
    fontWeight: 'bold',
  },
  subscribersText: {
    color: '#aaa',
    fontSize: 12,
  },
  subscribeButton: {
    backgroundColor: '#333',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
  },
  subscribeButtonActive: {
    backgroundColor: '#555',
  },
  subscribeButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  actionsRow: {
    flexDirection: 'row',
    marginBottom: 16,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#222',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    marginRight: 8,
  },
  actionButtonText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 6,
  },
  commentsSection: {
    backgroundColor: '#222',
    padding: 12,
    borderRadius: 12,
    marginBottom: 24,
  },
  commentsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  commentsTitle: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
    marginRight: 8,
  },
  commentsCount: {
    color: '#aaa',
    fontSize: 14,
  },
  commentPreview: {
    marginTop: 4,
  },
  commentItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  commentAvatar: {
    width: 24,
    height: 24,
    borderRadius: 12,
    marginRight: 10,
    marginTop: 2,
  },
  commentTextContainer: {
    flex: 1,
  },
  commentText: {
    color: '#fff',
    fontSize: 13,
    lineHeight: 18,
  },
  viewAllComments: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#333',
    alignItems: 'center',
  },
  viewAllText: {
    color: '#3ea6ff',
    fontSize: 14,
    fontWeight: '600',
  },
  noCommentsText: {
    color: '#aaa',
    fontSize: 14,
    textAlign: 'center',
    paddingVertical: 8,
  },
  relatedSection: {
    marginTop: 8,
  },
  relatedTitle: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 16,
  },
});

export default VideoPlaybackScreen;
